import React, {useContext, useEffect, useMemo} from 'react';
import {
    DoppeActionsSourceType,
    DoppeActionType,
    doppeActionTypeResolveSettingsFromAction
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {DoppeDtoAction} from '../../client-server-common/types/doppe-dto-action';
import {BymoPageViewerMode, useBymoPageViewerContext} from '../bymo-page-viewer-context/bymo-page-viewer-context';
import {useDoppeViewerClientServices} from '../../client/doppe-user-app/doppe-viewer-client-services';
import {IDoppeViewerActionContext, useBymoPageContextProps} from '../../doppe-sdk';
import {assertDefined, assertNotNullable, ObservableValue} from '@wix/devzai-utils-common';
import {
    DoppeViewerActionContext,
    DoppeViewerActionContextLocator
} from '../doppe-viewer-action-context/doppe-viewer-action-context';
import {ModalsScopeContext, reactCreateMemo, useObservableCalculatedValue} from '@wix/devzai-utils-react';
import {DoppeBiEmptyTargetUrl} from '../../client-server-common';
import {DoppeViewerModalsController} from '../doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import type {DoppeViewerActionsList} from "../doppe-viewer-actions-list/doppe-viewer-actions-list";
import {
    useWidgetRenderingContext
} from "../doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context";

export const DoppeViewerActionRenderer = reactCreateMemo(function DoppeViewerActionRenderer<PROPS extends {}> (
    props: DoppeViewerActionRenderer.Props<PROPS>
) {
    const {
        action,
        actionTogglingController,
        actionsListController,
        actionIndex = -1,
        children,
        actionContextLocator,
        ignoreActionViewedEvent = false,
        openedFromSearch = false,

        widgetRenderingContextType,
        observableIsRenderingInnerState,
        observableCurrentItemIdToRender
    } = props;

    const clientServices = useDoppeViewerClientServices();
    const bymoPageContextProps = useBymoPageContextProps();

    const widgetRenderingContext = useWidgetRenderingContext();

    const actionType = assertDefined(
        clientServices.actionTypesStore.getRegisteredActionType(action.actionTypeId),
        `Action type with id '${action.actionTypeId}' isn't registered`);

    const viewerContext = useBymoPageViewerContext();

    useEffect(() => {
        if (viewerContext.mode === BymoPageViewerMode.Live && !ignoreActionViewedEvent) {
            if (widgetRenderingContext?.lockController) {
                clientServices.viewerAnalyticsClient.logEvent('uouLockActionViewed', {
                    lockedActionId: widgetRenderingContext?.lockController.lockedActionId,
                    actionId: action.id,
                    pageId: viewerContext.bymoPageId,
                    hypeUserId: viewerContext.doppeUserId,
                    linkId: viewerContext.linkId,
                    itemPosition: actionIndex,
                    actionType: action.actionTypeId,
                    destinationUrl: bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContext.pageType,
                    lockSource: openedFromSearch ? DoppeActionsSourceType.Search : DoppeActionsSourceType.Page
                });
            }
            else {
                clientServices.viewerAnalyticsClient.logEvent('uouActionViewed', {
                    actionId: action.id,
                    pageId: viewerContext.bymoPageId,
                    hypeUserId: viewerContext.doppeUserId,
                    linkId: viewerContext.linkId,
                    itemPosition: actionIndex,
                    actionType: action.actionTypeId,
                    destinationUrl: bymoPageContextProps.targetUrl ?? DoppeBiEmptyTargetUrl,
                    pageType: viewerContext.pageType,
                    sourceType: openedFromSearch ? DoppeActionsSourceType.Search : DoppeActionsSourceType.Page
                });
            }

        }
    }, []);

    const modalsScope = assertNotNullable(useContext(ModalsScopeContext));

    const actionContext = useMemo(() => {
        return new DoppeViewerActionContext<PROPS>({
            openedFromSearch: openedFromSearch,
            action: {
                ...action,
                //TODO:: should remove it since this logic is already runs as part of the migration logic
                settings: doppeActionTypeResolveSettingsFromAction(actionType, action)
            },
            widgetRenderingContextType: widgetRenderingContextType,
            actionsListController: actionsListController,
            actionIndex: actionIndex,
            clientServices: clientServices,
            viewerContextData: viewerContext,
            bymoPageContextProps: bymoPageContextProps,
            actionTogglingController: actionTogglingController,
            modalsController: new DoppeViewerModalsController({
                modalsScope: modalsScope
            }),
            lockController: widgetRenderingContext?.lockController,
            observableCurrentItemIdToRender: observableCurrentItemIdToRender,
            observableIsRenderingInnerState: observableIsRenderingInnerState
        });
    }, [action, actionType, actionIndex, clientServices, viewerContext, bymoPageContextProps, actionTogglingController, modalsScope, observableIsRenderingInnerState, observableCurrentItemIdToRender, widgetRenderingContext?.lockController]);

    actionContextLocator?.set(action.id, actionContext);

    const observableActionContext = useObservableCalculatedValue(actionContext as IDoppeViewerActionContext<PROPS>);

    return children(actionContext, actionType, observableActionContext)
});

export namespace DoppeViewerActionRenderer {

    export interface Props<PROPS extends {}> {
        actionContextLocator?: DoppeViewerActionContextLocator;
        action: DoppeDtoAction;
        actionTogglingController?: DoppeViewerActionContext.ActionTogglingController;
        actionsListController?: DoppeViewerActionsList;
        actionIndex?: number;
        ignoreActionViewedEvent?: boolean;
        openedFromSearch?: boolean;


        observableCurrentItemIdToRender: ObservableValue<string | null> | undefined;
        observableIsRenderingInnerState: ObservableValue<boolean>;
        widgetRenderingContextType: DoppeViewerActionContext.CreationOptions<any>['widgetRenderingContextType']

        children: (
            actionContext: IDoppeViewerActionContext<PROPS>,
            actionType: DoppeActionType<any>,
            observableActionContext: ObservableValue<IDoppeViewerActionContext<PROPS>>,
        ) => React.ReactElement;
    }
}
